import { Autocomplete, TextField, CircularProgress, Checkbox, Typography } from '@mui/material';
import { CheckBox, CheckBoxOutlineBlank, DescriptionOutlined } from '@mui/icons-material';
import useFetchCampaignsForCalls from '@/components/calls/CampaignSelector/useFetchCampaignsForCalls';
import type { Campaign } from '@/components/campaigns/types';

type ProjectMultiSelectProps = {
    value: number[];
    isAnalyzing: boolean;
    onChange: (ids: number[]) => void;
};

// Sentinel row at the top of the list that resets the selection to "all projects".
const ALL_PROJECTS_ID = 0;
const ALL_PROJECTS_OPTION = { id: ALL_PROJECTS_ID, name: 'All Projects' } as unknown as Campaign;

// Load the full project list once and filter it client-side inside the dropdown.
// (No per-keystroke server search — typing narrows the list already in memory.)
const PROJECT_FETCH_LIMIT = 1000;

const ProjectMultiSelect: React.FC<ProjectMultiSelectProps> = ({ value, isAnalyzing, onChange }) => {
    const { data: campaigns = [], isLoading } = useFetchCampaignsForCalls({ limit: PROJECT_FETCH_LIMIT });

    const options = [ALL_PROJECTS_OPTION, ...campaigns];
    const selected = campaigns.filter((c) => value.includes(c.id));

    const optionLabel = (c: Campaign): string => c.name || `Project ${c.id}`;

    const handleChange = (_event: unknown, next: Campaign[]): void => {
        // Choosing "All Projects" clears the selection (empty = all projects).
        if (next.some((c) => c.id === ALL_PROJECTS_ID)) {
            onChange([]);
            return;
        }
        onChange(next.map((c) => c.id));
    };

    return (
        <Autocomplete
            disabled={isAnalyzing}
            multiple
            disableCloseOnSelect
            disableClearable
            size="small"
            options={options}
            value={selected}
            loading={isLoading}
            getOptionLabel={optionLabel}
            isOptionEqualToValue={(o, v) => o.id === v.id}
            filterOptions={(opts, state) => {
                const query = state.inputValue.trim().toLowerCase();
                if (!query) return opts;
                return opts.filter((o) => o.id !== ALL_PROJECTS_ID && optionLabel(o).toLowerCase().includes(query));
            }}
            onChange={handleChange}
            // Compact summary instead of one chip per project, so the control keeps a
            // fixed size no matter how many projects are selected.
            renderValue={() => (
                <Typography variant="body2" noWrap sx={{ pl: 0.5, color: 'text.primary' }}>
                    {value.length} project{value.length > 1 ? 's' : ''} selected
                </Typography>
            )}
            renderOption={(props, option, { selected: isSel }) => {
                const isAll = option.id === ALL_PROJECTS_ID;
                const checked = isAll ? value.length === 0 : isSel;
                return (
                    <li
                        {...props}
                        key={option.id}
                        style={{
                            ...props.style,
                            paddingLeft: '5px',
                        }}
                    >
                        <Checkbox
                            icon={<CheckBoxOutlineBlank fontSize="small" />}
                            checkedIcon={<CheckBox fontSize="small" />}
                            checked={checked}
                            sx={{ mr: 0 }}
                        />
                        {option.name || `Project ${option.id}`}
                    </li>
                );
            }}
            renderInput={(params) => (
                <TextField
                    {...params}
                    placeholder={value.length === 0 ? 'All Projects' : ''}
                    slotProps={{
                        input: {
                            ...params.InputProps,
                            startAdornment: (
                                <>
                                    <DescriptionOutlined fontSize="small" sx={{ color: 'text.secondary', ml: 0.4 }} />
                                    {params.InputProps.startAdornment}
                                </>
                            ),
                            endAdornment: (
                                <>
                                    {isLoading ? <CircularProgress size={18} /> : null}
                                    {params.InputProps.endAdornment}
                                </>
                            ),
                        },
                    }}
                />
            )}
            sx={{
                width: 225, // fixed so the banner layout doesn't shift as projects are selected
                bgcolor: 'background.paper',
                borderRadius: 2,
                '& .MuiOutlinedInput-root': {
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    flexWrap: 'nowrap', // keep the summary + input on one line
                },
                '& .MuiOutlinedInput-notchedOutline': {
                    borderRadius: 2,
                },
                '& .MuiAutocomplete-inputRoot': {
                    borderRadius: 2,
                },
            }}
        />
    );
};

export default ProjectMultiSelect;
